import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  DEFAULT_TOOLBAR_CONFIG,
  EDITOR_COMMANDS,
  type EditorToolbarConfig,
  GROUP_LABELS,
} from "@norkron/web-elaborate-editor";
import { api } from "../api";
import { ApiError, type WorkspaceSettings } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Button, Card, TextField } from "../compose/controls";
import { ShieldCheck, SlidersHorizontal } from "../compose/Icon";
import { EmptyState, ErrorState, Skeleton } from "../compose/states";
import { useToast } from "../compose/Toast";
import { ACCENTS, resolveAccent } from "../lib/accents";
import { useAsync } from "../lib/useAsync";
import { useTheme } from "../theme/ThemeProvider";

/**
 * Settings `/w/:slug/settings` (SETTINGS slice). Two sections:
 *   · Workspace (staff-only) — display name + accent, PATCH
 *     /api/workspaces/:slug/settings. Rendered only for a caller who may
 *     administer this workspace (global staff OR the `staff` membership role);
 *     everyone else sees an honest access-scoped note. The server is the real
 *     gate (403) — this is only the UI mirror.
 *   · Your profile (everyone) — the caller's own display name, PATCH /api/me.
 *
 * Deliberately small: the IA keeps configuration out of the reading room
 * (ia-and-flows §0) — this is the one quiet place for the two settings that
 * exist today. Styled with the existing design-system tokens via the
 * `settings.css` section skin (padded section cards, a header/footer rhythm,
 * on-brand accent-color controls) + the shared `.card`/`.field`/`.segmented`
 * classes — theme-aware in light and dark, no hardcoded colors.
 */

export function Settings() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { user, memberships } = useAuth();

  const role = memberships.find((m) => m.workspace_slug === slug)?.role;
  const canManageWorkspace = Boolean(user?.is_staff) || role === "staff";

  return (
    <div className="page settings" data-testid="settings-page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Settings</h1>
          <p className="page__sub">Workspace appearance and your profile.</p>
        </div>
      </div>

      {canManageWorkspace ? (
        <>
          <WorkspaceSection slug={slug} />
          <EditorToolbarSection slug={slug} />
        </>
      ) : (
        <Card className="settings-section">
          <EmptyState
            icon={<ShieldCheck size={28} strokeWidth={1.5} />}
            title="Workspace settings are staff-only"
            body="The workspace name and accent are managed by staff. You can still update your own profile below."
          />
        </Card>
      )}

      <ProfileSection />
    </div>
  );
}

// ── loading placeholders ─────────────────────────────────────────────────────
// Content-shaped skeletons that mirror each section's final layout, so the
// reveal settles in place instead of popping from an empty card. They use the
// shared, surface-agnostic `Skeleton` primitive (theme-aware, visible on the
// elevated `.card` surface where plain grey bars used to blend into the card).

/** Workspace section placeholder: name label + input, then the accent row. */
function WorkspaceFormSkeleton() {
  return (
    <div className="settings-skeleton" data-testid="workspace-skeleton" aria-hidden>
      <Skeleton height={12} width={120} radius={4} />
      <Skeleton height={40} radius={8} />
      <Skeleton height={12} width={72} radius={4} />
      <div className="settings-skeleton__swatches">
        {Array.from({ length: ACCENT_KEYS.length }).map((_, i) => (
          <Skeleton key={i} height={30} width={30} radius={8} />
        ))}
      </div>
    </div>
  );
}

/** Editor-toolbar placeholder: the mode toggle, then a few command lanes. */
function ToolbarFormSkeleton() {
  return (
    <div className="settings-skeleton" data-testid="toolbar-skeleton" aria-hidden>
      <Skeleton height={34} width={180} radius={8} />
      {Array.from({ length: 3 }).map((_, lane) => (
        <div key={lane} className="settings-skeleton__lane">
          <Skeleton height={14} width={130} radius={4} />
          <div className="settings-skeleton__grid">
            <Skeleton height={16} width={150} radius={4} />
            <Skeleton height={16} width={120} radius={4} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── editor toolbar governance (EditorToolbarConfig) ──────────────────────────
// Ribbon vs simple mode + per-group / per-command enable-disable, persisted to
// the workspace via PATCH /api/workspaces/:slug/settings/editor-toolbar. The
// command catalog is the SINGLE source of truth (`EDITOR_COMMANDS`) — adding a
// command in the lib auto-adds its toggle here (RULE ZERO, no hand-rolled list).

/** The catalog grouped by lane (first-seen order), each command carrying its
 *  essential flag (essential commands can never be disabled). */
interface LaneRow {
  group: string;
  label: string;
  commands: { id: string; label: string; essential: boolean }[];
}
function laneRows(): LaneRow[] {
  const order: string[] = [];
  const map = new Map<string, LaneRow["commands"]>();
  for (const c of EDITOR_COMMANDS) {
    if (!map.has(c.group)) {
      map.set(c.group, []);
      order.push(c.group);
    }
    map.get(c.group)?.push({
      id: c.id,
      label: c.label,
      essential: Boolean(c.essential),
    });
  }
  return order.map((g) => ({
    group: g,
    label: GROUP_LABELS[g] ?? g,
    commands: map.get(g) ?? [],
  }));
}

/** Staff-only editor-toolbar governance form. */
function EditorToolbarSection({ slug }: { slug: string }) {
  const toast = useToast();
  const loaded = useAsync<EditorToolbarConfig>(
    () => api.getEditorToolbar(slug),
    [slug],
  );

  const [cfg, setCfg] = useState<EditorToolbarConfig>(DEFAULT_TOOLBAR_CONFIG);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lanes = useMemo(() => laneRows(), []);

  useEffect(() => {
    if (loaded.data) setCfg(loaded.data);
  }, [loaded.data]);

  const groupOff = (g: string) => cfg.disabledGroups.includes(g);
  const cmdOff = (id: string) => cfg.disabledCommandIds.includes(id);

  function toggleGroup(g: string, enable: boolean) {
    setCfg((c) => ({
      ...c,
      disabledGroups: enable
        ? c.disabledGroups.filter((x) => x !== g)
        : [...c.disabledGroups.filter((x) => x !== g), g],
    }));
  }
  function toggleCommand(id: string, enable: boolean) {
    setCfg((c) => ({
      ...c,
      disabledCommandIds: enable
        ? c.disabledCommandIds.filter((x) => x !== id)
        : [...c.disabledCommandIds.filter((x) => x !== id), id],
    }));
  }

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const stored = await api.updateEditorToolbar(slug, cfg);
      setCfg(stored);
      toast.show("Editor toolbar saved");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not save the toolbar.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="settings-section" data-testid="editor-toolbar-settings">
      <div className="settings-section__head">
        <span className="settings-section__icon">
          <SlidersHorizontal size={16} strokeWidth={1.5} />
        </span>
        <h2 className="settings-section__title">Editor toolbar</h2>
      </div>
      <p className="settings-section__hint">
        Choose the toolbar surface and turn command groups or individual commands
        on or off for everyone in this workspace.
      </p>

      {loaded.loading ? (
        <ToolbarFormSkeleton />
      ) : loaded.error && !(loaded.error instanceof ApiError) ? (
        <ErrorState body="We couldn't load the editor toolbar settings." />
      ) : (
        <>
          {error ? (
            <div className="auth__error" role="alert" style={{ marginTop: 12 }}>
              {error}
            </div>
          ) : null}

          <div
            className="segmented settings-modes"
            role="radiogroup"
            aria-label="Toolbar mode"
          >
            {(["ribbon", "simple"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={cfg.mode === m}
                data-testid={`ets-mode-${m}`}
                className={`segmented__opt${cfg.mode === m ? " segmented__opt--active" : ""}`}
                onClick={() => setCfg((c) => ({ ...c, mode: m }))}
              >
                {m === "ribbon" ? "Ribbon" : "Simple"}
              </button>
            ))}
          </div>

          <div className="ets-tree" data-testid="ets-command-tree">
            {lanes.map((lane) => {
              const on = !groupOff(lane.group);
              return (
                <div key={lane.group} className="ets-lane">
                  <label className="ets-lane__head">
                    <input
                      type="checkbox"
                      checked={on}
                      data-testid={`ets-group-toggle-${lane.group}`}
                      onChange={(e) => toggleGroup(lane.group, e.target.checked)}
                    />
                    {lane.label}
                  </label>
                  <div className="ets-grid">
                    {lane.commands.map((cmd) => {
                      const checked = cmd.essential || (on && !cmdOff(cmd.id));
                      const locked = cmd.essential || !on;
                      return (
                        <label
                          key={cmd.id}
                          className={`ets-cmd${locked ? " ets-cmd--locked" : ""}`}
                          title={cmd.id}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={locked}
                            data-testid={`ets-cmd-toggle-${cmd.id}`}
                            onChange={(e) =>
                              toggleCommand(cmd.id, e.target.checked)
                            }
                          />
                          {cmd.label}
                          {cmd.essential ? (
                            <span className="ets-cmd__star" aria-hidden="true">
                              ★
                            </span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="settings-section__actions">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              data-testid="ets-reset"
              onClick={() => setCfg(DEFAULT_TOOLBAR_CONFIG)}
            >
              Reset to default
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={busy}
              data-testid="ets-save"
              onClick={() => void save()}
            >
              {busy ? "Saving…" : "Save toolbar"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

/** The 8 archival accent hues, keyed by their palette token (lib/accents). */
const ACCENT_KEYS = Object.keys(ACCENTS);

/** Staff-only workspace appearance form. */
function WorkspaceSection({ slug }: { slug: string }) {
  const toast = useToast();
  const settings = useAsync<WorkspaceSettings>(
    () => api.getWorkspaceSettings(slug),
    [slug],
  );

  const [name, setName] = useState("");
  const [accent, setAccent] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form once the current settings load (or when the slug changes).
  useEffect(() => {
    if (settings.data) {
      setName(settings.data.name);
      setAccent(settings.data.accent);
    }
  }, [settings.data]);

  const forbidden =
    settings.error instanceof ApiError && settings.error.status === 403;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Enter a workspace name.");
      return;
    }
    setBusy(true);
    try {
      const saved = await api.updateWorkspaceSettings(slug, {
        name: name.trim(),
        accent,
      });
      setName(saved.name);
      setAccent(saved.accent);
      toast.show("Workspace settings saved");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not save the settings.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="settings-section" data-testid="workspace-settings">
      <div className="settings-section__head">
        <span className="settings-section__icon">
          <SlidersHorizontal size={16} strokeWidth={1.5} />
        </span>
        <h2 className="settings-section__title">Workspace</h2>
      </div>

      {settings.loading ? (
        <WorkspaceFormSkeleton />
      ) : forbidden ? (
        <EmptyState
          icon={<ShieldCheck size={28} strokeWidth={1.5} />}
          title="Staff only"
          body="Only workspace staff can change these settings."
        />
      ) : settings.error ? (
        <ErrorState body="We couldn't load the workspace settings." />
      ) : (
        <form onSubmit={submit}>
          {error ? (
            <div className="auth__error" role="alert">
              {error}
            </div>
          ) : null}
          <TextField
            label="Workspace name"
            value={name}
            data-testid="workspace-name"
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
          />
          <AccentPicker value={accent} onChange={setAccent} />
          <div className="settings-section__actions">
            <Button
              type="submit"
              variant="primary"
              disabled={busy}
              data-testid="save-workspace"
            >
              {busy ? "Saving…" : "Save workspace"}
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

/** A row of accent swatches — one real radio button per palette hue. */
function AccentPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (accent: string) => void;
}) {
  const { resolved } = useTheme();
  return (
    <div className="field">
      <span className="field__label" id="accent-label">
        Accent
      </span>
      <div
        className="settings-accents"
        role="radiogroup"
        aria-labelledby="accent-label"
      >
        {ACCENT_KEYS.map((key) => {
          const a = resolveAccent(key);
          const color = resolved === "dark" ? a.dark : a.light;
          const active = key === value;
          return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={key}
              title={key}
              data-testid={`accent-${key}`}
              onClick={() => onChange(key)}
              className={`settings-accent${active ? " settings-accent--active" : ""}`}
              style={{ background: color }}
            />
          );
        })}
      </div>
    </div>
  );
}

/** Everyone's own display-name form (PATCH /api/me). */
function ProfileSection() {
  const toast = useToast();
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the field in sync if the session user hydrates after mount.
  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Enter your name.");
      return;
    }
    setBusy(true);
    try {
      await api.updateMe({ name: name.trim() });
      await refresh();
      toast.show("Profile saved");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not save your profile.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="settings-section" data-testid="profile-settings">
      <div className="settings-section__head">
        <h2 className="settings-section__title">Your profile</h2>
      </div>
      <form onSubmit={submit}>
        {error ? (
          <div className="auth__error" role="alert">
            {error}
          </div>
        ) : null}
        <TextField
          label="Display name"
          value={name}
          data-testid="profile-name"
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
        />
        {user ? (
          <p className="settings-section__hint">
            Signed in as {user.email}
            {user.is_staff ? " · staff" : ""}
          </p>
        ) : null}
        <div className="settings-section__actions">
          <Button
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="save-profile"
          >
            {busy ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
